import { createSlice } from "@reduxjs/toolkit";

const device = ({
  deviceId,
  macAddress = deviceId,
  model = null,
  name,
  status = "online",
  type,
  wireType = "wired",
  children = [],
}) => ({
  deviceId,
  macAddress,
  model,
  name,
  status,
  type,
  wireType,
  children,
});

const coreTree = ({ edgeOnline = true, includeClient = true } = {}) =>
  device({
    deviceId: "40:86:CB:13:F1:00",
    model: "DMS-3130-30TS",
    name: "Core-Switch-01",
    type: "switch",
    children: [
      device({
        deviceId: "BC:22:28:E7:9D:AB",
        model: "DMS-1250-10SP",
        name: "Edge-SW-1F",
        status: edgeOnline ? "online" : "offline",
        type: "switch",
        children: includeClient
          ? [
            device({
              deviceId: "8C:88:3A:11:22:33",
              model: null,
              name: "Client-100",
              type: "client",
            }),
          ]
          : [],
      }),
      device({
        deviceId: "C4:E9:0A:2A:74:20",
        model: "DAP-X2810",
        name: "AP-Lobby",
        status: edgeOnline ? "online" : "offline",
        type: "ap",
      }),
    ],
  });

const detachedSwitch = (status = "online") =>
  device({
    deviceId: "08:5A:11:2B:1A:00",
    model: "DGS-3630-28TC",
    name: "Detached-Switch-01",
    status,
    type: "switch",
  });

const isolatedGateway = () =>
  device({
    deviceId: "48:EE:0C:AF:53:27",
    model: "DBR-600-P",
    name: "DBR-600-P 53:27",
    status: "offline",
    type: "gateway",
  });

const gatewayTree = () =>
  device({
    deviceId: "F0:B4:29:88:10:01",
    model: "DBG-X1000",
    name: "Gateway-01",
    type: "gateway",
    children: [
      device({
        deviceId: "50:C7:BF:24:92:11",
        model: "DAP-X2850",
        name: "AP-Meeting-02",
        type: "ap",
      }),
    ],
  });

const initialState = {
  data: {
    snapshots: [
      {
        dayIndex: 0,
        state: "ok",
        snapshotAt: 1786363200,
        tree: [coreTree(), gatewayTree(), detachedSwitch(), isolatedGateway()],
      },
      {
        dayIndex: 1,
        state: "ok",
        snapshotAt: 1786449600,
        tree: [coreTree({ includeClient: false }), detachedSwitch(), isolatedGateway()],
      },
      {
        dayIndex: 2,
        state: "ok",
        snapshotAt: 1786536000,
        tree: [coreTree({ edgeOnline: false }), gatewayTree(), isolatedGateway()],
      },
      {
        dayIndex: 3,
        state: "noSnapshot",
        snapshotAt: null,
        tree: null,
      },
      {
        dayIndex: 4,
        state: "ok",
        snapshotAt: 1786708800,
        tree: [gatewayTree(), detachedSwitch("offline"), isolatedGateway()],
      },
      {
        dayIndex: 5,
        state: "ok",
        snapshotAt: 1786795200,
        tree: [coreTree(), detachedSwitch(), isolatedGateway()],
      },
      {
        dayIndex: 6,
        state: "ok",
        snapshotAt: 1786881600,
        tree: [coreTree({ includeClient: false }), isolatedGateway()],
      },
    ],
  },
};

export const getSummaryReportTopologyMock = ({ summaryReportId, reports }) => {
  const report = reports?.find(
    (item) => item.summaryReportId === summaryReportId,
  );
  const weekStart = report?.weekStart;

  return {
    data: {
      snapshots: initialState.data.snapshots.map((snapshot) => {
        if (!weekStart || snapshot.state !== "ok") {
          return { ...snapshot };
        }

        const snapshotDate = new Date(`${weekStart}T12:00:00Z`);
        snapshotDate.setUTCDate(snapshotDate.getUTCDate() + snapshot.dayIndex);
        return {
          ...snapshot,
          snapshotAt: Math.floor(snapshotDate.getTime() / 1000),
        };
      }),
    },
  };
};

const summaryReportTopologySlice = createSlice({
  name: "summaryReportTopology",
  initialState,
});

export default summaryReportTopologySlice.reducer;
